import React, { useState, useMemo } from "react";
import Icon from "components/AppIcon";
import Tooltip from "components/Tooltip";

const PRESETS = [
  { label: 'Crisis -10%', benchmarkShock: -10, eurUsdShock: 0 },
  { label: 'Caída leve -5%', benchmarkShock: -5, eurUsdShock: 0 },
  { label: 'Subida +5%', benchmarkShock: 5, eurUsdShock: 0 },
  { label: 'Subida fuerte +10%', benchmarkShock: 10, eurUsdShock: 0 },
];

const SHOCK_TABS = [
  { id: 'benchmark', label: 'Benchmark' },
  { id: 'eurusd', label: 'EUR/USD' },
  { id: 'combined', label: 'Combinado' },
];

const TIME_HORIZONS = [
  { id: 'today', label: 'Hoy', volatilityFactor: 1.0 },
  { id: '7d', label: '7 días', volatilityFactor: 1.08 },
  { id: '30d', label: '30 días', volatilityFactor: 1.18 },
];

export default function ScenarioSimulator({ baseExposure, batches, benchmarks }) {
  const [scope, setScope] = useState('all');
  const [shockTab, setShockTab] = useState('benchmark');
  const [benchmarkShock, setBenchmarkShock] = useState(-5);
  const [eurUsdShock, setEurUsdShock] = useState(0);
  const [timeHorizon, setTimeHorizon] = useState('today');
  const [benchmarkShockInput, setBenchmarkShockInput] = useState('-5');
  const [eurUsdShockInput, setEurUsdShockInput] = useState('0');

  // Derive active metals from batches
  const activeMetals = useMemo(() => {
    const metals = new Set();
    batches?.filter(b => ['En Espera', 'Procesando', 'Listo para Venta']?.includes(b?.batch_status))
      ?.forEach(b => { if (b?.metal_name) metals?.add(b?.metal_name); });
    return Array.from(metals);
  }, [batches]);

  const activeCategories = useMemo(() => {
    const cats = new Set();
    batches?.filter(b => ['En Espera', 'Procesando', 'Listo para Venta']?.includes(b?.batch_status))
      ?.forEach(b => { if (b?.category_name || b?.scrap_category) cats?.add(b?.category_name || b?.scrap_category); });
    return Array.from(cats);
  }, [batches]);

  // Compute scoped exposure
  const scopedBatches = useMemo(() => {
    const active = batches?.filter(b => ['En Espera', 'Procesando', 'Listo para Venta']?.includes(b?.batch_status)) || [];
    if (scope === 'all') return active;
    if (scope?.startsWith('metal:')) {
      const metal = scope?.replace('metal:', '');
      return active?.filter(b => b?.metal_name === metal);
    }
    if (scope?.startsWith('cat:')) {
      const cat = scope?.replace('cat:', '');
      return active?.filter(b => (b?.category_name || b?.scrap_category) === cat);
    }
    return active;
  }, [batches, scope]);

  const scopedExposure = useMemo(() =>
    scopedBatches?.reduce((sum, b) => sum + (parseFloat(b?.purchase_price) || 0) * (parseFloat(b?.weight_t) || 0), 0),
    [scopedBatches]
  );

  const totalWeight = useMemo(() =>
    scopedBatches?.reduce((sum, b) => sum + (parseFloat(b?.weight_t) || 0), 0),
    [scopedBatches]
  );

  // Compute impact
  const horizon = TIME_HORIZONS?.find(h => h?.id === timeHorizon);
  const vf = horizon?.volatilityFactor || 1;

  const effectiveShock = useMemo(() => {
    if (shockTab === 'benchmark') return benchmarkShock;
    if (shockTab === 'eurusd') return eurUsdShock * 0.6; // EUR/USD affects ~60% of exposure
    return benchmarkShock + eurUsdShock * 0.6;
  }, [shockTab, benchmarkShock, eurUsdShock]);

  const portfolioImpact = (scopedExposure * effectiveShock * vf) / 100;
  const isLoss = portfolioImpact < 0;
  const avgMarginResult = totalWeight > 0 ? portfolioImpact / totalWeight : 0;

  // Batches that would go into loss
  const batchesInLoss = useMemo(() => {
    return scopedBatches?.filter(b => {
      const exp = (parseFloat(b?.purchase_price) || 0) * (parseFloat(b?.weight_t) || 0);
      const impact = (exp * effectiveShock * vf) / 100;
      return impact < 0 && Math.abs(impact) > exp * 0.01;
    });
  }, [scopedBatches, effectiveShock, vf]);

  // Auto recommendation
  const impactPct = scopedExposure > 0 ? (portfolioImpact / scopedExposure) * 100 : 0;
  const mostExposedMetal = useMemo(() => {
    const byMetal = {};
    scopedBatches?.forEach(b => {
      const m = b?.metal_name || 'Otros';
      byMetal[m] = (byMetal?.[m] || 0) + (parseFloat(b?.purchase_price) || 0) * (parseFloat(b?.weight_t) || 0);
    });
    return Object.entries(byMetal)?.sort((a, b) => b?.[1] - a?.[1])?.[0]?.[0] || 'el metal más expuesto';
  }, [scopedBatches]);

  const recommendation = useMemo(() => {
    if (impactPct < -5) return { text: `Considera acelerar ventas en ${mostExposedMetal}`, icon: 'AlertTriangle', color: 'var(--color-error)' };
    if (impactPct > 0) return { text: 'Escenario favorable para compra', icon: 'TrendingUp', color: 'var(--color-success)' };
    return { text: 'Impacto moderado — monitoriza posiciones', icon: 'Info', color: 'var(--color-warning)' };
  }, [impactPct, mostExposedMetal]);

  const applyPreset = (preset) => {
    setBenchmarkShock(preset?.benchmarkShock);
    setBenchmarkShockInput(String(preset?.benchmarkShock));
    setEurUsdShock(preset?.eurUsdShock);
    setEurUsdShockInput(String(preset?.eurUsdShock));
    setShockTab('benchmark');
  };

  const handleBenchmarkInput = (val) => {
    setBenchmarkShockInput(val);
    const n = parseFloat(val);
    if (!isNaN(n) && n >= -20 && n <= 20) setBenchmarkShock(n);
  };

  const handleEurUsdInput = (val) => {
    setEurUsdShockInput(val);
    const n = parseFloat(val);
    if (!isNaN(n) && n >= -20 && n <= 20) setEurUsdShock(n);
  };

  return (
    <div className="card p-4 lg:p-5 min-w-0 flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Icon name="Sliders" size={18} color="var(--color-accent)" strokeWidth={2} />
        <Tooltip
          title="Simulador de Escenarios"
          description="Calcula el impacto en tu cartera de distintos movimientos de mercado antes de que ocurran. Úsalo para tomar decisiones de compra/venta con información sobre el riesgo."
        >
          <h3 className="font-heading font-semibold text-base text-foreground">Simulador de Escenarios</h3>
        </Tooltip>
      </div>
      {/* Preset buttons */}
      <div className="flex flex-col gap-1.5">
        <Tooltip
          title="Escenarios Predefinidos"
          description="Atajos rápidos para los escenarios más habituales. Rellenan los sliders automáticamente para que puedas comparar impactos en segundos."
        >
          <span className="text-xs font-caption text-muted-foreground font-medium">Escenarios rápidos</span>
        </Tooltip>
        <div className="flex flex-wrap gap-1.5">
          {PRESETS?.map(p => (
            <button
              key={p?.label}
              onClick={() => applyPreset(p)}
              className="px-2.5 py-1 rounded-md text-xs font-caption font-medium border transition-standard hover:opacity-80"
              style={{
                borderColor: p?.benchmarkShock < 0 ? 'var(--color-error)' : 'var(--color-success)',
                color: p?.benchmarkShock < 0 ? 'var(--color-error)' : 'var(--color-success)',
                background: 'transparent',
              }}
            >
              {p?.label}
            </button>
          ))}
        </div>
      </div>
      {/* Scope selector */}
      <div className="flex flex-col gap-1.5">
        <Tooltip
          title="Alcance de la Simulación"
          description="Todo el inventario: simula el impacto global. Por metal: aísla el efecto en un metal concreto. Por categoría: analiza una calidad específica de scrap."
        >
          <label className="text-xs font-caption text-muted-foreground font-medium">Alcance</label>
        </Tooltip>
        <select
          value={scope}
          onChange={e => setScope(e?.target?.value)}
          className="w-full text-sm font-caption rounded-md border border-border px-3 py-1.5 bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-accent"
          style={{ background: 'var(--color-card)', color: 'var(--color-foreground)', borderColor: 'var(--color-border)' }}
        >
          <option value="all">Todo el inventario</option>
          {activeMetals?.map(m => (
            <option key={m} value={`metal:${m}`}>Metal: {m}</option>
          ))}
          {activeCategories?.map(c => (
            <option key={c} value={`cat:${c}`}>Categoría: {c}</option>
          ))}
        </select>
      </div>
      {/* Shock type tabs */}
      <div className="flex flex-col gap-2">
        <Tooltip
          title="Tipo de Shock"
          description="Benchmark: simula una subida o bajada del precio del metal. EUR/USD: simula un movimiento del tipo de cambio. Combinado: aplica ambos shocks simultáneamente para un escenario más realista."
        >
          <label className="text-xs font-caption text-muted-foreground font-medium">Tipo de shock</label>
        </Tooltip>
        <div className="flex gap-1 p-1 rounded-lg border border-border" style={{ background: 'var(--color-muted)20' }}>
          {SHOCK_TABS?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setShockTab(tab?.id)}
              className="flex-1 py-1.5 px-2 rounded-md text-xs font-caption font-medium transition-standard"
              style={{
                background: shockTab === tab?.id ? 'var(--color-card)' : 'transparent',
                color: shockTab === tab?.id ? 'var(--color-accent)' : 'var(--color-muted-foreground)',
                boxShadow: shockTab === tab?.id ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
              }}
            >
              {tab?.label}
            </button>
          ))}
        </div>
      </div>
      {/* Benchmark shock slider */}
      {(shockTab === 'benchmark' || shockTab === 'combined') && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <Tooltip
              title="Magnitud del Shock (%)"
              description="Porcentaje de variación del benchmark respecto al precio actual. Negativo = caída de precio. Positivo = subida. Desliza o escribe el valor directamente."
            >
              <label className="text-xs font-caption text-muted-foreground font-medium">Shock Benchmark</label>
            </Tooltip>
            <input
              type="number"
              value={benchmarkShockInput}
              onChange={e => handleBenchmarkInput(e?.target?.value)}
              min={-20} max={20} step={0.5}
              className="w-16 text-xs font-data text-center rounded border border-border px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-accent"
              style={{ background: 'var(--color-card)', color: 'var(--color-foreground)', borderColor: 'var(--color-border)' }}
            />
          </div>
          <div className="relative flex items-center">
            <span className="text-xs font-data text-muted-foreground w-8">-20%</span>
            <div className="relative flex-1 mx-2">
              <input
                type="range"
                min={-20} max={20} step={0.5}
                value={benchmarkShock}
                onChange={e => { setBenchmarkShock(parseFloat(e?.target?.value)); setBenchmarkShockInput(e?.target?.value); }}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, ${
                    benchmarkShock < 0 ? 'var(--color-error)' : 'var(--color-border)'
                  } 0%, ${
                    benchmarkShock < 0 ? 'var(--color-error)' : 'var(--color-success)'
                  } ${((benchmarkShock + 20) / 40) * 100}%, var(--color-border) ${((benchmarkShock + 20) / 40) * 100}%, var(--color-border) 100%)`,
                  accentColor: benchmarkShock < 0 ? 'var(--color-error)' : 'var(--color-success)',
                }}
              />
            </div>
            <span className="text-xs font-data text-muted-foreground w-8 text-right">+20%</span>
          </div>
        </div>
      )}
      {/* EUR/USD shock slider */}
      {(shockTab === 'eurusd' || shockTab === 'combined') && (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <Tooltip
              title="Shock EUR/USD (%)"
              description="Variación del tipo de cambio EUR/USD. Una subida del EUR frente al USD reduce el precio en euros de los metales LME, aunque el precio en USD no cambie."
            >
              <label className="text-xs font-caption text-muted-foreground font-medium">Shock EUR/USD</label>
            </Tooltip>
            <input
              type="number"
              value={eurUsdShockInput}
              onChange={e => handleEurUsdInput(e?.target?.value)}
              min={-20} max={20} step={0.5}
              className="w-16 text-xs font-data text-center rounded border border-border px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-accent"
              style={{ background: 'var(--color-card)', color: 'var(--color-foreground)', borderColor: 'var(--color-border)' }}
            />
          </div>
          <div className="relative flex items-center">
            <span className="text-xs font-data text-muted-foreground w-8">-20%</span>
            <div className="relative flex-1 mx-2">
              <input
                type="range"
                min={-20} max={20} step={0.5}
                value={eurUsdShock}
                onChange={e => { setEurUsdShock(parseFloat(e?.target?.value)); setEurUsdShockInput(e?.target?.value); }}
                className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, ${
                    eurUsdShock < 0 ? 'var(--color-error)' : 'var(--color-border)'
                  } 0%, ${
                    eurUsdShock < 0 ? 'var(--color-error)' : 'var(--color-success)'
                  } ${((eurUsdShock + 20) / 40) * 100}%, var(--color-border) ${((eurUsdShock + 20) / 40) * 100}%, var(--color-border) 100%)`,
                  accentColor: eurUsdShock < 0 ? 'var(--color-error)' : 'var(--color-success)',
                }}
              />
            </div>
            <span className="text-xs font-data text-muted-foreground w-8 text-right">+20%</span>
          </div>
        </div>
      )}
      {/* Time horizon */}
      <div className="flex flex-col gap-1.5">
        <Tooltip
          title="Horizonte Temporal"
          description="Hoy: impacto inmediato sobre precios actuales. 7 días / 30 días: proyección ajustada por la volatilidad histórica del metal en esa ventana temporal."
        >
          <label className="text-xs font-caption text-muted-foreground font-medium">Horizonte temporal</label>
        </Tooltip>
        <div className="flex gap-1">
          {TIME_HORIZONS?.map(h => (
            <button
              key={h?.id}
              onClick={() => setTimeHorizon(h?.id)}
              className="flex-1 py-1.5 text-xs font-caption font-medium rounded-md border transition-standard"
              style={{
                borderColor: timeHorizon === h?.id ? 'var(--color-accent)' : 'var(--color-border)',
                background: timeHorizon === h?.id ? 'var(--color-accent)15' : 'transparent',
                color: timeHorizon === h?.id ? 'var(--color-accent)' : 'var(--color-muted-foreground)',
              }}
            >
              {h?.label}
            </button>
          ))}
        </div>
      </div>
      {/* Output */}
      <div
        className="p-4 rounded-lg border"
        style={{
          background: isLoss ? 'var(--color-error)08' : 'var(--color-success)08',
          borderColor: isLoss ? 'var(--color-error)30' : 'var(--color-success)30',
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <Tooltip
            title="Impacto en Cartera (€)"
            description="Ganancia o pérdida total en euros sobre tu exposición actual si el escenario simulado se materializase hoy."
          >
            <span className="text-xs font-caption text-muted-foreground">Impacto en cartera</span>
          </Tooltip>
          <Icon
            name={isLoss ? 'TrendingDown' : 'TrendingUp'}
            size={16}
            color={isLoss ? 'var(--color-error)' : 'var(--color-success)'}
            strokeWidth={2}
          />
        </div>
        <p
          className="font-data text-2xl font-bold mb-1"
          style={{ color: isLoss ? 'var(--color-error)' : 'var(--color-success)' }}
        >
          {portfolioImpact >= 0 ? '+' : ''}{portfolioImpact?.toLocaleString('es-ES', { maximumFractionDigits: 0 })} €
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mb-3">
          <span className="text-xs font-caption text-muted-foreground">
            Margen medio resultante:{' '}
            <span className="font-data font-semibold" style={{ color: avgMarginResult >= 0 ? 'var(--color-success)' : 'var(--color-error)' }}>
              {avgMarginResult >= 0 ? '+' : ''}{avgMarginResult?.toFixed(1)} €/t
            </span>
          </span>
          <Tooltip
            title="Lotes que Entrarían en Pérdida"
            description="Lotes para los que el escenario simulado haría que el P&L latente se volviera negativo. Candidatos prioritarios para acelerar la venta."
          >
            <span className="text-xs font-caption text-muted-foreground">
              Lotes en pérdida:{' '}
              <span className="font-data font-semibold" style={{ color: batchesInLoss?.length > 0 ? 'var(--color-error)' : 'var(--color-success)' }}>
                {batchesInLoss?.length}
              </span>
            </span>
          </Tooltip>
        </div>
        {batchesInLoss?.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-caption text-muted-foreground mb-1">Lotes afectados:</p>
            <div className="flex flex-wrap gap-1">
              {batchesInLoss?.slice(0, 5)?.map(b => (
                <span
                  key={b?.id}
                  className="text-xs font-data px-1.5 py-0.5 rounded"
                  style={{ background: 'var(--color-error)15', color: 'var(--color-error)' }}
                >
                  {b?.batch_code || b?.metal_name}
                </span>
              ))}
              {batchesInLoss?.length > 5 && (
                <span className="text-xs font-caption text-muted-foreground">+{batchesInLoss?.length - 5} más</span>
              )}
            </div>
          </div>
        )}
        {/* Recommendation */}
        <Tooltip
          title="Recomendación"
          description="Sugerencia automática basada en el resultado de la simulación. No es asesoramiento financiero — es una referencia operativa basada en los parámetros configurados."
        >
          <div
            className="flex items-start gap-2 p-2.5 rounded-md"
            style={{ background: recommendation?.color + '12' }}
          >
            <Icon name={recommendation?.icon} size={13} color={recommendation?.color} strokeWidth={2} className="flex-shrink-0 mt-0.5" />
            <p className="text-xs font-caption" style={{ color: recommendation?.color }}>
              <span className="font-semibold">Recomendación: </span>{recommendation?.text}
            </p>
          </div>
        </Tooltip>
      </div>
    </div>
  );
}